import { Box, IconButton, Switch, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { Client } from '../../../data/models/client.model';
import { ServicePlanServiceArea } from '../../../data/models/client-program.model';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'ServiceAreaListDeprecated';

const classes = {
  container: `${PREFIX}-container`,
  serviceArea: `${PREFIX}-serviceArea`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'stretch',
    gap: theme.spacing(1),
  },

  [`& .${classes.serviceArea}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#bcf5e1',
    borderRadius: 4,
    padding: theme.spacing(0.875),
  },
}));

interface ServiceAreaListDeprecatedProps {
  client: Client;
  serviceAreas: ServicePlanServiceArea[];
  onActivate: (serviceArea: ServicePlanServiceArea) => void;
  onEdit: (serviceArea: ServicePlanServiceArea) => void;
  onDeactivate: (serviceArea: ServicePlanServiceArea) => void;
  onDelete: (serviceArea: ServicePlanServiceArea) => void;
  onStartDrag: (serviceArea: ServicePlanServiceArea) => void;
  onEndDrag: (serviceArea: ServicePlanServiceArea) => void;
}

export default function ServiceAreaListDeprecated({
  client,
  serviceAreas,
  onActivate,
  onDeactivate,
  onEdit,
  onDelete,
  onStartDrag,
  onEndDrag,
}: ServiceAreaListDeprecatedProps) {
  const t = useCommonTranslation();

  const isSuperReaderUser = useIsSuperReaderUser();

  const handleActiveChange = (
    evt: any,
    serviceArea: ServicePlanServiceArea,
  ) => {
    if (evt.target.checked) {
      onActivate(serviceArea);
    } else {
      onDeactivate(serviceArea);
    }
  };

  return (
    <StyledBox className={classes.container}>
      {serviceAreas.map((serviceArea) => (
        <Box
          key={serviceArea.serviceAreaUUID}
          className={classes.serviceArea}
          draggable={serviceArea.state === 'active'}
          onDragStart={() => onStartDrag(serviceArea)}
          onDragEnd={() => onEndDrag(serviceArea)}>
          <Box className={classes.serviceArea}>{serviceArea.name}</Box>
          <Box>
            {!isSuperReaderUser && (
              <Tooltip title={t('client.editServiceArea')}>
                <span>
                  <IconButton
                    data-testid="edit-service-area"
                    onClick={() => onEdit(serviceArea)}
                    color="primary"
                    size="large"
                    disabled={serviceArea.state === 'active'}>
                    <EditOutlinedIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {!isSuperReaderUser && (
              <Tooltip title={t('client.deleteServiceArea')}>
                <span>
                  <IconButton
                    data-testid="delete-service-area"
                    onClick={() => onDelete(serviceArea)}
                    color="error"
                    size="large"
                    disabled={serviceArea.state === 'active'}>
                    <DeleteForeverIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {!isSuperReaderUser && (
              <Tooltip
                title={
                  serviceArea.state === 'active'
                    ? t('client.active')
                    : t('client.inactive')
                }>
                <Switch
                  checked={serviceArea.state === 'active'}
                  onChange={(evt) => handleActiveChange(evt, serviceArea)}
                />
              </Tooltip>
            )}
          </Box>
        </Box>
      ))}
    </StyledBox>
  );
}
